import { ArrowLeft, Plus, Save, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import ConfirmarVentaPosModal from '../../components/pos/ConfirmarVentaPosModal'
import FormularioVentaPos, {
  Campo,
} from '../../components/pos/FormularioVentaPos'
import ModalMensaje from '../../components/ui/ModalMensaje'
import { useEmpresa } from '../../context/EmpresaContext'
import { useModalMensaje } from '../../hooks/useModalMensaje'
import { supabase } from '../../lib/supabase'
import { formatearMedioPago } from '../../utils/gastos'
import { normalizarMoneda, redondearMoneda } from '../../utils/numeros'
import {
  ESTADOS_ENVASES_POS,
  OPCIONES_MEDIO_PAGO_POS,
  formatearFechaPos,
  formatearKilosPos,
  formatearMontoPos,
  obtenerEstadoPagoNuevaVentaPos,
  obtenerFechaActualPos,
} from '../../utils/pos'

// Medios de pago que además de monto admiten una referencia (N.º de
// transferencia o de cheque). El resto (efectivo, tarjetas, otro) no la
// necesita en la pantalla de Nueva venta.
const MEDIOS_CON_REFERENCIA = ['transferencia', 'cheque']

// Tolerancia mínima solo para la comparación en el frontend (nunca para
// el backend, que sigue siendo la autoridad con numeric(16,2)). Cubre
// residuos de punto flotante que puedan quedar pese al redondeo por
// línea; no oculta diferencias reales de un peso o más.
const TOLERANCIA_COMPARACION_MONTOS = 0.001

// Monto de pago: dinero, no decimal físico — un punto es separador de
// miles ("30.000" -> 30000). Ver normalizarMoneda.
function convertirMonto(valor) {
  const numero = normalizarMoneda(valor)

  return Number.isFinite(numero) ? numero : 0
}

function crearPagoVacio(idLocal) {
  return {
    idLocal,
    medio_pago: '',
    monto: '',
    referencia: '',
  }
}

function NuevaVentaPos() {
  const navigate = useNavigate()
  const { empresaActual } = useEmpresa()
  const { modal, abrirModal, cerrarModal } = useModalMensaje()

  const esConsulta = empresaActual?.rol === 'consulta'

  const [pagos, setPagos] = useState([])

  const [totales, setTotales] = useState({
    subtotalVenta: 0,
    montoDescuento: 0,
    totalVenta: 0,
    totalKilos: 0,
  })

  const [datosPendientes, setDatosPendientes] = useState(null)
  const [resumenConfirmacion, setResumenConfirmacion] =
    useState(null)
  const [confirmando, setConfirmando] = useState(false)
  const [registrando, setRegistrando] = useState(false)

  const contadorPagoRef = useRef(0)

  const valoresIniciales = useMemo(() => {
    if (!empresaActual?.id) return null

    return {
      fecha: obtenerFechaActualPos(),
      clienteId: '',
      observaciones: '',
      estadoEnvases: 'pendiente',
      tipoDescuento: 'ninguno',
      valorDescuento: '',
      filas: [],
    }
  }, [empresaActual?.id])

  useEffect(() => {
    contadorPagoRef.current = 0
    setPagos([])
  }, [empresaActual?.id])

  // Si el total de la venta queda en $0 (p. ej. descuento del 100%), no
  // tiene sentido mantener pagos armados: se limpian para no enviarlos.
  useEffect(() => {
    if (totales.totalVenta <= 0 && pagos.length > 0) {
      setPagos([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totales.totalVenta])

  const pagosConMonto = pagos.map((pago) => ({
    ...pago,
    montoNumero: convertirMonto(pago.monto),
  }))

  const totalPagado = redondearMoneda(
    pagosConMonto.reduce(
      (acumulado, pago) => acumulado + pago.montoNumero,
      0
    )
  )

  const saldo = totales.totalVenta - totalPagado

  const estadoPagoActual = obtenerEstadoPagoNuevaVentaPos(
    totales.totalVenta,
    totalPagado
  )

  const siguienteIdPago = () => {
    contadorPagoRef.current += 1
    return contadorPagoRef.current
  }

  const agregarPago = () => {
    if (totales.totalVenta <= 0) return

    setPagos((actuales) => [
      ...actuales,
      crearPagoVacio(siguienteIdPago()),
    ])
  }

  const eliminarPago = (idLocal) => {
    setPagos((actuales) =>
      actuales.filter((pago) => pago.idLocal !== idLocal)
    )
  }

  const cambiarCampoPago = (idLocal, campo, valor) => {
    setPagos((actuales) =>
      actuales.map((pago) =>
        pago.idLocal === idLocal ? { ...pago, [campo]: valor } : pago
      )
    )
  }

  const construirPagos = (datos) => {
    if (datos.totalVenta <= 0) return []

    return pagosConMonto.map((pago) => ({
      fecha_pago: new Date().toISOString(),
      medio_pago: pago.medio_pago,
      monto: pago.montoNumero,
      referencia: pago.referencia.trim() || null,
      observaciones: null,
    }))
  }

  const manejarValidSubmit = (datos) => {
    if (datos.totalVenta > 0) {
      for (const pago of pagosConMonto) {
        if (!pago.medio_pago) {
          abrirModal({
            tipo: 'error',
            titulo: 'Medio de pago requerido',
            mensaje:
              'Debes seleccionar el medio de pago en cada pago agregado.',
          })
          return
        }

        if (pago.montoNumero <= 0) {
          abrirModal({
            tipo: 'error',
            titulo: 'Monto inválido',
            mensaje:
              'El monto de cada pago debe ser mayor que cero.',
          })
          return
        }
      }

      if (
        totalPagado >
        datos.totalVenta + TOLERANCIA_COMPARACION_MONTOS
      ) {
        abrirModal({
          tipo: 'error',
          titulo: 'Pagos superan el total',
          mensaje:
            'El total de los pagos no puede superar el total de la venta.',
        })
        return
      }
    }

    setDatosPendientes(datos)

    setResumenConfirmacion({
      clienteLabel:
        datos.clienteSeleccionado?.razon_social ||
        'Venta sin cliente',
      fechaLabel: formatearFechaPos(datos.fecha),
      cantidadProductos: datos.filas.length,
      totalKilosLabel: formatearKilosPos(datos.totalKilos),
      subtotalLabel: formatearMontoPos(datos.subtotalVenta),
      descuento:
        datos.tipoDescuento === 'ninguno'
          ? null
          : {
              etiqueta:
                datos.tipoDescuento === 'porcentaje'
                  ? `Descuento ${datos.valorDescuento || 0}%`
                  : 'Descuento',
              montoLabel: `- ${formatearMontoPos(
                datos.montoDescuento
              )}`,
            },
      totalVentaLabel: formatearMontoPos(datos.totalVenta),
      estadoEnvasesLabel:
        ESTADOS_ENVASES_POS[datos.estadoEnvases].etiqueta,
      montoEnvasesPagadoLabel:
        datos.estadoEnvases === 'pagado'
          ? formatearMontoPos(datos.montoEnvasesPagadoNumero)
          : null,
      pagos: pagosConMonto.map((pago) => ({
        idLocal: pago.idLocal,
        medioLabel: formatearMedioPago(pago.medio_pago),
        montoLabel: formatearMontoPos(pago.montoNumero),
        referencia: pago.referencia.trim() || null,
      })),
      totalPagadoLabel: formatearMontoPos(totalPagado),
      saldoLabel: formatearMontoPos(
        Math.max(datos.totalVenta - totalPagado, 0)
      ),
    })

    setConfirmando(true)
  }

  const confirmarVenta = async () => {
    if (registrando || !datosPendientes) return

    try {
      setRegistrando(true)

      const payload = {
        p_empresa_id: empresaActual.id,
        p_cliente_id: datosPendientes.clienteId || null,
        p_fecha_venta: datosPendientes.fecha,
        p_estado_envases: datosPendientes.estadoEnvases,
        p_monto_envases_pagado: datosPendientes.montoEnvasesPagadoNumero,
        p_tipo_descuento: datosPendientes.tipoDescuento,
        p_valor_descuento:
          datosPendientes.tipoDescuento === 'ninguno'
            ? 0
            : datosPendientes.valorDescuentoNumero,
        p_observaciones:
          datosPendientes.observaciones.trim() || null,
        p_detalles: datosPendientes.detallesPayload,
        p_pagos: construirPagos(datosPendientes),
      }

      const { error } = await supabase.rpc(
        'registrar_venta_pos',
        payload
      )

      if (error) throw error

      setConfirmando(false)

      abrirModal({
        tipo: 'success',
        titulo: 'Venta registrada',
        mensaje: 'Venta registrada correctamente.',
        onAceptar: () => {
          cerrarModal()
          navigate('/pos/ventas', { state: { recargar: true } })
        },
      })
    } catch (errorRegistro) {
      console.error('Error al registrar venta POS:', errorRegistro)

      setConfirmando(false)

      abrirModal({
        tipo: 'error',
        titulo: 'No fue posible registrar la venta',
        mensaje:
          errorRegistro.message ||
          'No fue posible registrar la venta.',
      })
    } finally {
      setRegistrando(false)
    }
  }

  if (esConsulta) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
        <h3 className="text-lg font-bold text-slate-900">
          Sin permisos
        </h3>

        <p className="mt-2 text-slate-500">
          Tu perfil no tiene permisos para registrar ventas.
        </p>
      </div>
    )
  }

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={() => navigate('/pos/ventas')}
        className="mb-5 flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-emerald-700"
      >
        <ArrowLeft size={18} />
        Volver a ventas
      </button>

      <div className="mb-8">
        <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
          {empresaActual?.nombre_fantasia}
        </p>

        <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
          Nueva venta
        </h2>

        <p className="mt-2 text-slate-500">
          Registra productos, kilos y pagos de la venta.
        </p>
      </div>

      <FormularioVentaPos
        empresaId={empresaActual?.id}
        valoresIniciales={valoresIniciales}
        bloqueado={registrando}
        textoBoton="Registrar venta"
        iconoBoton={Save}
        onCancelar={() => navigate('/pos/ventas')}
        onValidSubmit={manejarValidSubmit}
        onCambiarTotales={setTotales}
      >
        <div className="mt-8 border-t border-slate-200 pt-6">
          <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Pagos
            </h3>

            {totales.totalVenta > 0 && (
              <button
                type="button"
                onClick={agregarPago}
                disabled={registrando}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-600 px-4 py-2.5 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                <Plus size={18} />
                Agregar forma de pago
              </button>
            )}
          </div>

          {totales.totalVenta <= 0 ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
              <p className="font-semibold text-slate-700">
                Venta sin monto a pagar
              </p>
              <p className="mt-1">
                Total venta {formatearMontoPos(totales.totalVenta)}
              </p>
            </div>
          ) : (
            <>
              {pagos.length === 0 && (
                <p className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
                  Aún no has agregado pagos. La venta quedará
                  registrada como pago pendiente.
                </p>
              )}

              <div className="space-y-4">
                {pagos.map((pago, indice) => {
                  const mostrarReferencia =
                    MEDIOS_CON_REFERENCIA.includes(pago.medio_pago)

                  return (
                    <div
                      key={pago.idLocal}
                      className="rounded-xl border border-slate-200 p-4"
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                          Pago {indice + 1}
                        </p>

                        <button
                          type="button"
                          onClick={() =>
                            eliminarPago(pago.idLocal)
                          }
                          disabled={registrando}
                          title="Eliminar pago"
                          aria-label="Eliminar pago"
                          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-red-200 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Campo label="Medio de pago" requerido>
                          <select
                            value={pago.medio_pago}
                            onChange={(event) =>
                              cambiarCampoPago(
                                pago.idLocal,
                                'medio_pago',
                                event.target.value
                              )
                            }
                            disabled={registrando}
                            className={estiloInput}
                          >
                            <option value="">
                              Seleccionar medio
                            </option>

                            {OPCIONES_MEDIO_PAGO_POS.map(
                              (opcion) => (
                                <option
                                  key={opcion.valor}
                                  value={opcion.valor}
                                >
                                  {opcion.etiqueta}
                                </option>
                              )
                            )}
                          </select>
                        </Campo>

                        <Campo label="Monto" requerido>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={pago.monto}
                            onChange={(event) =>
                              cambiarCampoPago(
                                pago.idLocal,
                                'monto',
                                event.target.value
                              )
                            }
                            placeholder="0"
                            disabled={registrando}
                            className={estiloInput}
                          />
                        </Campo>

                        {mostrarReferencia && (
                          <div className="sm:col-span-2">
                            <Campo
                              label={
                                pago.medio_pago === 'cheque'
                                  ? 'N° cheque / referencia'
                                  : 'Referencia (opcional)'
                              }
                            >
                              <input
                                type="text"
                                value={pago.referencia}
                                onChange={(event) =>
                                  cambiarCampoPago(
                                    pago.idLocal,
                                    'referencia',
                                    event.target.value
                                  )
                                }
                                placeholder={
                                  pago.medio_pago === 'cheque'
                                    ? 'Ej: CH-001245'
                                    : 'Ej: TRX-123456'
                                }
                                disabled={registrando}
                                className={estiloInput}
                              />
                            </Campo>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <ResumenPago
                    etiqueta="Total venta"
                    valor={formatearMontoPos(totales.totalVenta)}
                  />

                  <ResumenPago
                    etiqueta="Total pagado"
                    valor={formatearMontoPos(totalPagado)}
                  />

                  <ResumenPago
                    etiqueta="Saldo pendiente"
                    valor={formatearMontoPos(Math.max(saldo, 0))}
                  />
                </div>

                <p
                  className={`mt-4 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${estadoPagoActual.clase}`}
                >
                  {estadoPagoActual.etiqueta}
                </p>
              </div>
            </>
          )}
        </div>
      </FormularioVentaPos>

      <ConfirmarVentaPosModal
        abierto={confirmando}
        resumen={resumenConfirmacion}
        procesando={registrando}
        onCancelar={() => setConfirmando(false)}
        onConfirmar={confirmarVenta}
      />

      <ModalMensaje {...modal} />
    </div>
  )
}

const estiloInput =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-3 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100'

function ResumenPago({ etiqueta, valor }) {
  return (
    <div className="rounded-lg bg-white p-3 text-center shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {etiqueta}
      </p>

      <p className="mt-1 text-sm font-bold text-slate-900">
        {valor}
      </p>
    </div>
  )
}

export default NuevaVentaPos
